import { faLinkedin } from "@fortawesome/free-brands-svg-icons"
import {
  faLocationCrosshairs,
  faMailbox,
} from "@fortawesome/pro-regular-svg-icons"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { CoreButton } from "component/w24/component/CoreButton"
import { FieldInput } from "component/w24/input/FieldInput"
import { Localization } from "libs/localization/localization"
import { EColors, EFonts, EVariables } from "libs/style/w24_styling"
import { isValidEmail, sendEmail } from "libs/utils/api/api"
import { useIsWide } from "libs/utils/logic/responsiveLogic"
import React, { useState } from "react"
import styled from "styled-components/macro"

interface Props {
  className?: string
}

export const ContactForm = (props: Props): JSX.Element => {
  const isWide = useIsWide()
  const [email, setEmail] = useState("")
  const [message, setMessage] = useState("")
  const [clickedInAnOutEmail, setClickedInAnOutEmail] = useState(false)
  const [finished, setFinished] = useState<false | "success" | "error">(false)

  function isFilled(): boolean {
    return email && message && isValidEmail(email)
  }

  function isFilledInvalidEmail(): boolean {
    return email && !isValidEmail(email) && clickedInAnOutEmail
  }

  return (
    <S_ContactForm className={props.className}>
      <S_Socials>
        <S_Social>
          <S_Icon icon={faLinkedin} />
          <S_Link
            href="https://www.linkedin.com/in/jan-kondula-72974295/"
            target="blank"
          >
            Jan Kondula
          </S_Link>
        </S_Social>
        <S_Social>
          <S_Icon icon={faMailbox} />
          <S_Text>jan@mapheim.com</S_Text>
        </S_Social>
      </S_Socials>
      {!finished && (
        <>
          <S_Email
            label={
              isFilledInvalidEmail()
                ? Localization.EMAIL + " (" + Localization.EMAIL_NOT_VALID + ")"
                : Localization.EMAIL
            }
            multiple={false}
            value={email}
            onChange={value => {
              setEmail(value)
            }}
            onBlur={() => {
              setClickedInAnOutEmail(true)
            }}
          />
          <S_Message
            label={Localization.MESSAGE}
            multiple={true}
            value={message}
            onChange={value => {
              setMessage(value)
            }}
            rows={isWide ? 4 : 3}
          />
          <S_Button>
            <CoreButton
              title={Localization.SEND_ACTION}
              icon={faLocationCrosshairs}
              disabled={!isFilled()}
              onClick={async () => {
                if (isFilled()) {
                  try {
                    await sendEmail(".", email, message)
                    setFinished("success")
                  } catch (error) {
                    setFinished("error")
                  }
                }
              }}
            />
          </S_Button>
        </>
      )}
      {finished === "success" && (
        <S_Done>{Localization.MESSAGE_RECEIVED}</S_Done>
      )}
      {finished === "error" && <S_Done>{Localization.MESSAGE_ERROR}</S_Done>}
    </S_ContactForm>
  )
}

const S_ContactForm = styled.div`
  display: flex;
  flex-direction: column;
  padding: 25px;
  border-radius: ${EVariables.Radius2};
  background-color: ${EColors.GREY2};
  width: 100%;
  align-items: flex-end;
  @media only screen and (min-width: 1260px) {
    align-items: flex-start;
    padding: 40px 50px 0 50px;
  }
`

const S_Socials = styled.div`
  display: flex;
  width: 100%;
  margin-bottom: 15px;
  flex-direction: column;
  @media only screen and (min-width: 1260px) {
    flex-direction: row;
  }
`

const S_Social = styled.div`
  display: flex;
  align-items: center;
  margin-right: 50px;
`

const S_Icon = styled(FontAwesomeIcon)`
  width: 25px;
  font-size: 20px;
  color: ${EColors.DARK};
  @media only screen and (min-width: 1260px) {
    width: 30px;
    font-size: 25px;
  }
`

const S_Link = styled.a`
  margin-left: 15px;
  color: ${EColors.DARK};
  font-family: ${EFonts.Medium2};
  font-size: 15px;
  line-height: 37px;
  text-decoration: underline;
  cursor: pointer;
  &:hover {
    text-decoration: none;
  }
  @media only screen and (min-width: 1260px) {
    margin-left: 20px;
    font-size: 18px;
    line-height: 37px;
  }
`

const S_Text = styled.div`
  margin-left: 15px;
  color: ${EColors.DARK};
  font-family: ${EFonts.Medium2};
  font-size: 15px;
  line-height: 37px;
  @media only screen and (min-width: 1260px) {
    margin-left: 20px;
    font-size: 18px;
    line-height: 37px;
  }
`

const S_Done = styled.div`
  color: ${EColors.DARK};
  font-family: ${EFonts.Medium2};
  font-size: 18px;
  line-height: 37px;
`

const S_Email = styled(FieldInput)`
  @media only screen and (min-width: 1260px) {
    width: 380px;
    margin: 20px 0 0 0;
  }
`

const S_Message = styled(FieldInput)`
  margin: 20px 0 20px 0;
  min-height: 111px;
`

const S_Button = styled.div`
  width: 100%;
  display: flex;
  @media only screen and (min-width: 1260px) {
    justify-content: flex-end;
    margin: 18px 0px 0 0;
  }
`
